import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, FileText, Plus, Pencil, Download, CheckCircle2, Lock, History, Filter, X, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, downloadAuthed } from "@/lib/api";
import { fmtDuration15, fmtTime15, fmtDate, fmtTime, todayLocal, dayOf, toZoneInput, fromZoneInput, zoneNote } from "@/lib/format";
import { DepotPicker, DepotTag } from "@/components/DepotPicker";
import { useDepot } from "@/context/DepotContext";
import { usePay } from "@/context/CompanyContext";
import { periodStartISO, periodLabel, periodWord } from "@/lib/payPeriod";

export const TimesheetsTab = () => {
  const [rows, setRows] = useState([]);
  const [crew, setCrew] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ user_id: "", date: todayLocal(), start_time: "07:00", end_time: "15:30", break_min: "30", depot_id: "" });
  const { needsPick } = useDepot();
  const pay = usePay();
  const mondayISO = (iso) => periodStartISO(iso, pay);
  const word = periodWord(pay);

  const [edit, setEdit] = useState(null);
  const [editForm, setEditForm] = useState({ clock_in: "", clock_out: "", break_min: "0", depot_id: "" });

  const [weekStart, setWeekStart] = useState(() => mondayISO(new Date().toISOString()));
  useEffect(() => { setWeekStart(periodStartISO(new Date(), pay)); }, [pay.period, pay.start_day, pay.anchor, pay.timezone]); // eslint-disable-line react-hooks/exhaustive-deps
  const [fWorker, setFWorker] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [approvals, setApprovals] = useState({}); // user_id -> approval doc
  const [exports, setExports] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [payrollEmail, setPayrollEmail] = useState("");
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailing, setEmailing] = useState(false);

  const load = async () => {
    try {
      const [ts, ws] = await Promise.all([
        api.coordinatorTimesheets(),
        api.workers().catch(() => []),
      ]);
      setRows(ts);
      setCrew(ws);
    } catch { toast.error("Failed to load timesheets"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const loadApprovals = async () => {
    try {
      const a = await api.approvals(weekStart);
      setApprovals(Object.fromEntries((a || []).map((x) => [x.user_id, x])));
    } catch { /* local endpoint */ }
  };
  useEffect(() => { loadApprovals(); }, [weekStart]);
  useEffect(() => { api.payrollExports().then(setExports).catch(() => {}); }, []);
  useEffect(() => { api.reportSettings().then((s) => setPayrollEmail(s?.payroll_email || "")).catch(() => {}); }, []);

  // Per-worker totals for the selected week (rounded to 15 min, decimal hours).
  const weekSummary = useMemo(() => {
    const qtr = (s) => Math.round((Math.max(0, s || 0) / 3600) * 4) / 4;
    const map = new Map();
    rows.forEach((s) => {
      if (mondayISO(s.clock_in) !== weekStart) return;
      const id = s.user_id;
      if (!id) return;
      if (!map.has(id)) map.set(id, { user_id: id, name: s.user_name || s.user_email || id, seconds: 0, shifts: 0 });
      const w = map.get(id);
      w.seconds += (s.worked_seconds || 0);
      w.shifts += 1;
    });
    return [...map.values()].map((w) => ({ ...w, hours: qtr(w.seconds) })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, weekStart]);

  const toggleApprove = async (userId, approved) => {
    try {
      const res = await api.approveWeek(userId, weekStart, approved);
      setApprovals((prev) => {
        const next = { ...prev };
        if (approved) next[userId] = res; else delete next[userId];
        return next;
      });
      toast.success(approved ? `Pay ${word} approved` : "Approval removed");
    } catch { toast.error("Could not update approval"); }
  };

  // Worker picker: full crew roster (so a worker who forgot to clock on entirely
  // can still be selected), merged with any workers who already have shifts.
  const workers = useMemo(() => {
    const map = new Map();
    crew.forEach((w) => { if (w.id) map.set(w.id, { id: w.id, name: w.name || w.email || w.id }); });
    rows.forEach((s) => { if (s.user_id && !map.has(s.user_id)) map.set(s.user_id, { id: s.user_id, name: s.user_name || s.user_email || s.user_id }); });
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [rows, crew]);

  // Shifts shown in the table + used by "Export filtered CSV".
  const filteredRows = useMemo(() => rows.filter((s) => {
    if (fWorker !== "all" && s.user_id !== fWorker) return false;
    const day = dayOf(s.clock_in);
    if (fFrom && day < fFrom) return false;
    if (fTo && day > fTo) return false;
    return true;
  }), [rows, fWorker, fFrom, fTo]);
  const filtersActive = fWorker !== "all" || !!fFrom || !!fTo;
  const clearFilters = () => { setFWorker("all"); setFFrom(""); setFTo(""); };

  // Per-worker hours for the currently-shown (filtered) shifts — payroll review.
  const filteredSummary = useMemo(() => {
    const qtr = (s) => Math.round((Math.max(0, s || 0) / 3600) * 4) / 4;
    const map = new Map();
    filteredRows.forEach((s) => {
      const key = s.user_id || s.user_email || "unknown";
      if (!map.has(key)) map.set(key, { key, name: s.user_name || s.user_email || key, seconds: 0, shifts: 0 });
      const w = map.get(key);
      w.seconds += (s.worked_seconds || 0);
      w.shifts += 1;
    });
    const people = [...map.values()].map((w) => ({ ...w, hours: qtr(w.seconds) })).sort((a, b) => a.name.localeCompare(b.name));
    const totalHours = qtr(filteredRows.reduce((a, s) => a + (s.worked_seconds || 0), 0));
    return { people, totalHours };
  }, [filteredRows]);

  const buildCsv = (rowsList) => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const qtrHours = (s) => Math.round((Math.max(0, s || 0) / 3600) * 4) / 4;
    const fmtHrs = (h) => h.toFixed(2);
    const byWorker = new Map();
    rowsList.forEach((s) => {
      const key = s.user_email || s.user_id || "unknown";
      if (!byWorker.has(key)) byWorker.set(key, { name: s.user_name || key, email: s.user_email || "", shifts: [] });
      byWorker.get(key).shifts.push(s);
    });
    const ordered = [...byWorker.values()].sort((a, b) => a.name.localeCompare(b.name));
    const lines = [];

    // --- Per-worker payroll summary at the TOP for quick review ---
    lines.push(["PAYROLL SUMMARY"].map(esc).join(","));
    lines.push(["Worker", "Email", "Shifts", "Break (hrs)", "Worked (hrs)"].map(esc).join(","));
    let gShifts = 0, gBreak = 0, gWorked = 0;
    ordered.forEach((w) => {
      const wb = w.shifts.reduce((a, s) => a + qtrHours(s.break_seconds), 0);
      const ww = w.shifts.reduce((a, s) => a + qtrHours(s.worked_seconds), 0);
      gShifts += w.shifts.length; gBreak += wb; gWorked += ww;
      lines.push([w.name, w.email, w.shifts.length, fmtHrs(wb), fmtHrs(ww)].map(esc).join(","));
    });
    lines.push(["GRAND TOTAL", "", gShifts, fmtHrs(gBreak), fmtHrs(gWorked)].map(esc).join(","));
    lines.push("");

    // --- Shift-by-shift detail ---
    lines.push(["DETAIL"].map(esc).join(","));
    lines.push(["Worker", "Email", "Date", "Clock in", "Clock out", "Break (hrs)", "Worked (hrs)"].map(esc).join(","));
    ordered.forEach((w) => {
      const shifts = w.shifts.slice().sort((a, b) => new Date(a.clock_in) - new Date(b.clock_in));
      let ww = 0, wb = 0;
      shifts.forEach((s) => {
        const worked = qtrHours(s.worked_seconds); const brk = qtrHours(s.break_seconds);
        ww += worked; wb += brk;
        lines.push([w.name, w.email, fmtDate(s.clock_in), fmtTime(s.clock_in), s.clock_out ? fmtTime(s.clock_out) : "", fmtHrs(brk), fmtHrs(worked)].map(esc).join(","));
      });
      lines.push([w.name, w.email, "TOTAL", "", "", fmtHrs(wb), fmtHrs(ww)].map(esc).join(","));
    });
    return lines.join("\r\n");
  };

  const downloadCsv = (csv, filename) => {
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };

  const logExport = (list, weekStartForLog) => {
    const totalHours = Math.round((list.reduce((a, s) => a + (s.worked_seconds || 0), 0) / 3600) * 4) / 4;
    api.logPayrollExport({ week_start: weekStartForLog, rows: list.length, total_hours: totalHours })
      .then((rec) => setExports((prev) => [rec, ...prev]))
      .catch(() => {});
  };

  const exportCsv = () => {
    // Quick payroll export: ONLY the pay week selected in the approvals picker.
    const weekRows = rows.filter((s) => mondayISO(s.clock_in) === weekStart);
    if (weekRows.length === 0) { toast.error(`No shifts in the selected pay ${word}`); return; }
    downloadCsv(buildCsv(weekRows), `crossjobs-timesheets-week-${weekStart}.csv`);
    toast.success(`CSV downloaded — pay ${word} of ${fmtDate(weekStart)}`);
    logExport(weekRows, weekStart);
  };

  const exportFiltered = () => {
    if (filteredRows.length === 0) { toast.error("No shifts match the current filters"); return; }
    const tag = `${fFrom || "start"}_to_${fTo || "end"}`;
    downloadCsv(buildCsv(filteredRows), `crossjobs-timesheets-${tag}.csv`);
    toast.success(`CSV downloaded — ${filteredRows.length} shift${filteredRows.length !== 1 ? "s" : ""}`);
    logExport(filteredRows, fFrom || weekStart);
  };

  const payrollParams = () => {
    const p = {};
    if (filtersActive) {
      if (fWorker !== "all") p.user_id = fWorker;
      if (fFrom) p.start = fFrom;
      if (fTo) p.end = fTo;
    } else {
      p.week_start = weekStart;
    }
    return p;
  };

  const payrollPdf = async () => {
    const params = new URLSearchParams(payrollParams());
    try {
      await downloadAuthed(`/coordinator/payroll-summary.pdf?${params.toString()}`, "payroll-summary.pdf", true);
    } catch (e) { toast.error(e.message || "Could not open payroll PDF"); }
  };

  const sendPayroll = async (to) => {
    setEmailing(true);
    try {
      const r = await api.emailPayroll({ ...payrollParams(), ...(to ? { to } : {}) });
      setPayrollEmail(r.to);
      setEmailOpen(false);
      toast.success(`Payroll emailed to ${r.to}`);
    } catch (e) {
      if (e?.response?.status === 400) { setEmailOpen(true); }
      toast.error(e?.response?.data?.detail || "Could not email payroll");
    } finally { setEmailing(false); }
  };

  const emailPayrollClick = () => {
    if (payrollEmail) sendPayroll();
    else { setEmailTo(""); setEmailOpen(true); }
  };

  const openWeekPdf = async (row) => {
    try {
      await downloadAuthed(`/coordinator/timesheets/${row.user_id}/week.pdf?week_start=${mondayISO(row.clock_in)}`, `timesheet-${row.user_name || row.user_id}.pdf`, true);
    } catch (e) { toast.error(e.message || "Could not open PDF"); }
  };

  const submitAdd = async () => {
    const { user_id, date, start_time, end_time, break_min } = addForm;
    if (!user_id || !date || !start_time || !end_time) { toast.error("Pick a worker, date, start and end time"); return; }
    if (needsPick && !addForm.depot_id) { toast.error("Choose a depot for this shift"); return; }
    const clockIn = fromZoneInput(`${date}T${start_time}`);
    const clockOut = fromZoneInput(`${date}T${end_time}`);
    if (isNaN(clockIn) || isNaN(clockOut) || clockOut <= clockIn) { toast.error("End time must be after start time"); return; }
    setSaving(true);
    try {
      await api.addTimesheet({
        user_id,
        clock_in: clockIn.toISOString(),
        clock_out: clockOut.toISOString(),
        break_seconds: Math.max(0, parseInt(break_min, 10) || 0) * 60,
        depot_id: addForm.depot_id || undefined,
      });
      toast.success("Shift added");
      setAddOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add shift"); }
    finally { setSaving(false); }
  };

  const openEdit = (s) => {
    setEdit(s);
    setEditForm({
      clock_in: toZoneInput(s.clock_in),
      clock_out: toZoneInput(s.clock_out),
      break_min: String(Math.round((s.break_seconds || 0) / 60)),
      depot_id: s.depot_id || "",
    });
  };

  const submitEdit = async () => {
    if (!editForm.clock_in) { toast.error("Clock-in time is required"); return; }
    const clockIn = fromZoneInput(editForm.clock_in);
    const clockOut = editForm.clock_out ? fromZoneInput(editForm.clock_out) : null;
    if (isNaN(clockIn) || (clockOut && isNaN(clockOut))) { toast.error("Enter a valid date and time"); return; }
    if (clockOut && clockOut <= clockIn) { toast.error("Clock-out must be after clock-in"); return; }
    const breakMin = Math.max(0, parseInt(editForm.break_min, 10) || 0);
    // Upstream recomputes break from the `breaks` array on PATCH, so send a single
    // synthetic interval of the requested length starting at clock-in.
    const breaks = breakMin > 0
      ? [{ start: clockIn.toISOString(), end: new Date(clockIn.getTime() + breakMin * 60000).toISOString() }]
      : [];
    setSaving(true);
    try {
      await api.editTimesheet(edit.id, {
        clock_in: clockIn.toISOString(),
        clock_out: clockOut ? clockOut.toISOString() : null,
        breaks,
        break_seconds: breakMin * 60,
        ...(editForm.depot_id && editForm.depot_id !== edit.depot_id ? { depot_id: editForm.depot_id } : {}),
      });
      toast.success("Shift updated");
      setEdit(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update shift"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="font-display text-2xl font-bold">Timesheets</h2>
          <p className="text-sm text-muted-foreground">All crew shifts, most recent first. Add or correct a shift when a worker forgets to clock on or off.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button data-testid="payroll-history-btn" onClick={() => setShowHistory((v) => !v)} variant="ghost" className="rounded-full">
            <History className="w-4 h-4 mr-2" /> Export log ({exports.length})
          </Button>
          <Button data-testid="payroll-pdf-btn" onClick={payrollPdf} disabled={rows.length === 0} variant="outline" className="rounded-full" title="Printable per-worker payroll summary (respects the filters below, or the pay week)">
            <FileText className="w-4 h-4 mr-2" /> Payroll PDF
          </Button>
          <Button data-testid="email-payroll-btn" onClick={emailPayrollClick} disabled={rows.length === 0 || emailing} variant="outline" className="rounded-full" title="Email the payroll summary PDF to your bookkeeper">
            {emailing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />} Email payroll
          </Button>
          <Button data-testid="export-timesheets-csv-btn" onClick={exportCsv} disabled={weekSummary.length === 0} variant="outline" className="rounded-full" title={`Exports the pay ${word} starting ${fmtDate(weekStart)} (set it in the approvals panel below)`}>
            <Download className="w-4 h-4 mr-2" /> Export {word} CSV
          </Button>
          <Button data-testid="add-shift-manual-btn" onClick={() => setAddOpen(true)} disabled={workers.length === 0} className="rounded-full bg-primary hover:bg-primary/90 text-white">
            <Plus className="w-4 h-4 mr-2" /> Add shift
          </Button>
        </div>
      </div>

      {showHistory && (
        <div className="mb-6 rounded-xl border border-border bg-card p-4" data-testid="payroll-export-log">
          <p className="text-sm font-semibold mb-2">Payroll export history</p>
          {exports.length === 0 ? (
            <p className="text-xs text-muted-foreground">No exports yet. Use “Export CSV” to record one.</p>
          ) : (
            <div className="space-y-1.5">
              {exports.map((x) => (
                <div key={x.id} data-testid={`export-log-${x.id}`} className="flex items-center justify-between text-xs">
                  <span>{fmtDate(x.at)} {fmtTime(x.at)} · <span className="text-muted-foreground">{x.by}</span></span>
                  <span className="font-mono text-muted-foreground">{x.rows} shifts · {Number(x.total_hours).toFixed(2)}h</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mb-6 rounded-xl border border-border bg-card p-4" data-testid="weekly-approvals-panel">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <p className="text-sm font-semibold">Pay {word} approvals</p>
            <p className="text-xs text-muted-foreground" data-testid="pay-period-note">Pay {word} runs {periodLabel(pay)}. This {word} also drives “Export {word} CSV”.</p>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <Label className="text-muted-foreground">{word === "week" ? "Week" : "Fortnight"} starting</Label>
            <Input type="date" data-testid="approvals-week-input" value={weekStart} onChange={(e) => setWeekStart(e.target.value ? mondayISO(e.target.value) : weekStart)} className="h-8 w-auto" />
          </div>
        </div>
        {weekSummary.length === 0 ? (
          <p className="text-xs text-muted-foreground">No shifts recorded for this pay {word}.</p>
        ) : (
          <div className="space-y-1.5">
            {weekSummary.map((w) => {
              const appr = approvals[w.user_id];
              return (
                <div key={w.user_id} data-testid={`approval-row-${w.user_id}`} className="flex items-center justify-between gap-3 text-sm border-t border-border pt-2 first:border-0 first:pt-0">
                  <span className="font-medium">{w.name}</span>
                  <span className="font-mono text-muted-foreground">{w.hours.toFixed(2)}h · {w.shifts} shift{w.shifts !== 1 ? "s" : ""}</span>
                  {appr ? (
                    <Button variant="outline" size="sm" data-testid={`unapprove-${w.user_id}`} onClick={() => toggleApprove(w.user_id, false)} className="rounded-full h-8 text-emerald-600 border-emerald-300">
                      <Lock className="w-3.5 h-3.5 mr-1.5" /> Approved
                    </Button>
                  ) : (
                    <Button size="sm" data-testid={`approve-${w.user_id}`} onClick={() => toggleApprove(w.user_id, true)} className="rounded-full h-8 bg-primary hover:bg-primary/90 text-white">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Approve {word}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="mb-3 flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card p-3" data-testid="timesheet-filters">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground pb-2"><Filter className="w-4 h-4" /> Filter</div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">Worker</Label>
            <Select value={fWorker} onValueChange={setFWorker}>
              <SelectTrigger data-testid="filter-worker" className="h-9 w-44"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All workers</SelectItem>
                {workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">From</Label>
            <Input type="date" data-testid="filter-from" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="h-9 w-auto" />
          </div>
          <div className="space-y-1">
            <Label className="text-[11px] text-muted-foreground">To</Label>
            <Input type="date" data-testid="filter-to" value={fTo} onChange={(e) => setFTo(e.target.value)} className="h-9 w-auto" />
          </div>
          <Button variant="outline" size="sm" data-testid="export-filtered-csv-btn" onClick={exportFiltered} disabled={filteredRows.length === 0} className="rounded-full h-9">
            <Download className="w-4 h-4 mr-2" /> Export filtered ({filteredRows.length})
          </Button>
          {filtersActive && (
            <Button variant="ghost" size="sm" data-testid="clear-filters-btn" onClick={clearFilters} className="rounded-full h-9">
              <X className="w-4 h-4 mr-1.5" /> Clear
            </Button>
          )}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-border rounded-2xl"><p className="text-muted-foreground">No shifts logged yet. Once a worker clocks on, you can add or correct shifts here.</p></div>
      ) : filteredRows.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-2xl" data-testid="no-filter-match"><p className="text-muted-foreground">No shifts match the current filters.</p></div>
      ) : (
        <div className="bg-card border border-border rounded-2xl overflow-x-auto">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 border-b border-border bg-secondary/40 text-xs" data-testid="filtered-summary">
            <span className="font-semibold text-muted-foreground uppercase tracking-wide">Summary</span>
            {filteredSummary.people.map((w) => (
              <span key={w.key} data-testid={`summary-${w.name}`}><span className="font-medium">{w.name}</span> <span className="font-mono text-muted-foreground">{w.hours.toFixed(2)}h · {w.shifts}</span></span>
            ))}
            <span className="ml-auto font-semibold" data-testid="summary-total">Total {filteredSummary.totalHours.toFixed(2)}h · {filteredRows.length} shift{filteredRows.length !== 1 ? "s" : ""}</span>
          </div>
          <Table className="min-w-[760px]">
            <TableHeader>
              <TableRow>
                <TableHead>Worker</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Clock in</TableHead>
                <TableHead>Clock out</TableHead>
                <TableHead className="text-right">Break</TableHead>
                <TableHead className="text-right">Worked</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.map((s) => (
                <TableRow key={s.id} data-testid={`timesheet-row-${s.id}`}>
                  <TableCell className="font-medium"><span className="inline-flex items-center gap-2 flex-wrap">{s.user_name}<DepotTag id={s.depot_id} testId={`shift-row-depot-${s.id}`} /></span></TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(s.clock_in)}</TableCell>
                  <TableCell className="font-mono text-sm">
                    <div>{fmtTime15(s.clock_in)}</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`clockin-actual-${s.id}`}>{fmtTime(s.clock_in)}</div>
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {s.clock_out ? (
                      <>
                        <div>{fmtTime15(s.clock_out)}</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5" data-testid={`clockout-actual-${s.id}`}>{fmtTime(s.clock_out)}</div>
                      </>
                    ) : "—"}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm">
                    <div>{fmtDuration15(s.break_seconds)}</div>
                    {Array.isArray(s.breaks) && s.breaks.length > 0 && (
                      <div className="mt-0.5 space-y-0.5" data-testid={`timesheet-breaks-${s.id}`}>
                        {s.breaks.map((b, i) => (
                          <div key={i} className="text-[11px] text-muted-foreground">{fmtTime(b.start)}–{b.end ? fmtTime(b.end) : "…"}</div>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm font-semibold">{fmtDuration15(s.worked_seconds)}</TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${s.is_open ? "bg-emerald-500/15 text-emerald-600" : "bg-secondary text-muted-foreground"}`}>
                      {s.status === "on_break" ? "On break" : s.is_open ? "On shift" : "Done"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="icon" variant="ghost" data-testid={`timesheet-edit-${s.id}`} onClick={() => openEdit(s)} className="h-8 w-8 rounded-full hover:bg-secondary">
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" data-testid={`timesheet-pdf-${s.id}`} onClick={() => openWeekPdf(s)} className="h-8 w-8 rounded-full text-primary hover:bg-primary/10">
                      <FileText className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {rows.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2" data-testid="rounding-note">Break and worked times are rounded to the nearest 15 minutes. {zoneNote()}</p>
      )}

      {/* Add shift */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Add a shift</DialogTitle><DialogDescription>Manually log a shift when a worker forgets to clock on or off.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            {zoneNote() && <p className="text-xs text-amber-600" data-testid="add-shift-zone-note">{zoneNote()}</p>}
            <DepotPicker value={addForm.depot_id} onChange={(v) => setAddForm({ ...addForm, depot_id: v })} testId="add-shift-depot" />
            <div className="space-y-2">
              <Label>Worker *</Label>
              <Select value={addForm.user_id} onValueChange={(v) => setAddForm({ ...addForm, user_id: v })}>
                <SelectTrigger data-testid="add-shift-worker"><SelectValue placeholder="Select worker" /></SelectTrigger>
                <SelectContent>{workers.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input data-testid="add-shift-date" type="date" value={addForm.date} onChange={(e) => setAddForm({ ...addForm, date: e.target.value })} />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2"><Label>Start *</Label><Input data-testid="add-shift-start" type="time" value={addForm.start_time} onChange={(e) => setAddForm({ ...addForm, start_time: e.target.value })} /></div>
              <div className="space-y-2"><Label>End *</Label><Input data-testid="add-shift-end" type="time" value={addForm.end_time} onChange={(e) => setAddForm({ ...addForm, end_time: e.target.value })} /></div>
              <div className="space-y-2"><Label>Break (min)</Label><Input data-testid="add-shift-break" type="number" min="0" value={addForm.break_min} onChange={(e) => setAddForm({ ...addForm, break_min: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter><Button data-testid="add-shift-submit" onClick={submitAdd} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 text-white">{saving ? "Saving…" : "Add shift"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit shift */}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Edit shift</DialogTitle><DialogDescription>Correct the clock-in, clock-out or break for this shift.</DialogDescription></DialogHeader>
          {edit && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{edit.user_name} · {fmtDate(edit.clock_in)}</p>
              {zoneNote() && <p className="text-xs text-amber-600" data-testid="edit-shift-zone-note">{zoneNote()}</p>}
              <div className="space-y-2"><Label>Clock in *</Label><Input data-testid="edit-shift-in" type="datetime-local" value={editForm.clock_in} onChange={(e) => setEditForm({ ...editForm, clock_in: e.target.value })} /></div>
              <div className="space-y-2"><Label>Clock out <span className="text-muted-foreground font-normal">(leave blank to keep on-shift)</span></Label><Input data-testid="edit-shift-out" type="datetime-local" value={editForm.clock_out} onChange={(e) => setEditForm({ ...editForm, clock_out: e.target.value })} /></div>
              <div className="space-y-2"><Label>Break (min)</Label><Input data-testid="edit-shift-break" type="number" min="0" value={editForm.break_min} onChange={(e) => setEditForm({ ...editForm, break_min: e.target.value })} /></div>
              <DepotPicker always value={editForm.depot_id} onChange={(v) => setEditForm({ ...editForm, depot_id: v })} testId="edit-shift-depot" hint="Move this shift to the depot it was worked for." />
            </div>
          )}
          <DialogFooter><Button data-testid="edit-shift-submit" onClick={submitEdit} disabled={saving} className="rounded-full bg-primary hover:bg-primary/90 text-white">{saving ? "Saving…" : "Save changes"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email payroll to bookkeeper */}
      <Dialog open={emailOpen} onOpenChange={setEmailOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-xl">Email payroll to bookkeeper</DialogTitle><DialogDescription>We&apos;ll send the payroll summary PDF and remember this address so next time it&apos;s one tap.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            <Label>Bookkeeper email</Label>
            <Input data-testid="payroll-email-input" type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="bookkeeper@example.com" />
          </div>
          <DialogFooter><Button data-testid="payroll-email-send" disabled={emailing || !emailTo.trim()} onClick={() => sendPayroll(emailTo.trim())} className="rounded-full bg-primary hover:bg-primary/90 text-white">{emailing ? "Sending…" : "Send payroll"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
